/**
 * Composant pour afficher le statut de validation des clés API Stripe
 * Montre les résultats des tests et les erreurs/avertissements
 */

import React from 'react';
import {
    CheckCircle,
    XCircle,
    AlertTriangle,
    Shield,
    Key,
    Webhook,
    Lock,
    Loader2,
    AlertCircle,
    Info
} from 'lucide-react';
import type { StripeKeyValidationResult } from '@/services/stripe/stripe-key-validation-service';

interface StripeKeyValidationStatusProps {
    isLoading: boolean;
    result: StripeKeyValidationResult | null;
    onRetry?: () => void;
    /** Résultat optionnel du test de réception webhook */
    webhookTestResult?: {
        tested: boolean;
        received: boolean;
    };
}

const StripeKeyValidationStatus: React.FC<StripeKeyValidationStatusProps> = ({
    isLoading,
    result,
    onRetry,
    webhookTestResult,
}) => {
    if (isLoading) {
        return (
            <div className="bg-background-secondary rounded-xl p-6 border border-border">
                <div className="flex items-center justify-center space-x-3">
                    <Loader2 className="w-6 h-6 text-primary animate-spin" />
                    <div>
                        <p className="text-foreground font-medium">Validation en cours...</p>
                        <p className="text-sm text-foreground-secondary">
                            Test des clés API et vérification des permissions
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    if (!result && !webhookTestResult) {
        return null;
    }

    const getStatusIcon = (passed: boolean) => {
        return passed ? (
            <CheckCircle className="w-5 h-5 text-success" />
        ) : (
            <XCircle className="w-5 h-5 text-error" />
        );
    };

    return (
        <div>
            {/* Bouton Réessayer si erreur */}
            {onRetry && result && !result.isValid && (
                <div className="flex justify-end mb-2">
                    <button
                        onClick={onRetry}
                        className="px-3 py-1.5 text-sm font-medium text-foreground bg-background-secondary hover:bg-background-tertiary rounded-lg transition-colors"
                    >
                        Réessayer
                    </button>
                </div>
            )}

            {/* Tests individuels */}
            <div className="divide-y divide-border -mx-6">
                {/* Mode Test (affiché en premier si applicable) */}
                {result && result.isTestMode && (
                    <div className="py-3 px-6 flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 rounded-lg bg-warning/20 flex items-center justify-center">
                                <AlertTriangle className="w-4 h-4 text-warning" />
                            </div>
                            <div>
                                <p className="font-medium text-foreground">Mode Test</p>
                                <p className="text-sm text-foreground-secondary">
                                    Les paiements ne seront pas réels
                                </p>
                            </div>
                        </div>
                        <AlertTriangle className="w-5 h-5 text-warning" />
                    </div>
                )}

                {/* Test clé secrète */}
                {result && (
                    <div className="py-3 px-6 flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 rounded-lg bg-background-tertiary flex items-center justify-center">
                                <Key className="w-4 h-4 text-foreground-secondary" />
                            </div>
                            <div>
                                <p className="font-medium text-foreground">Clé secrète (SK)</p>
                                <p className="text-sm text-foreground-secondary">
                                    {result.tests.secretKey.message}
                                </p>
                            </div>
                        </div>
                        {getStatusIcon(result.tests.secretKey.passed)}
                    </div>
                )}

                {/* Test clé publique */}
                {result && (
                    <div className="py-3 px-6 flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 rounded-lg bg-background-tertiary flex items-center justify-center">
                                <Lock className="w-4 h-4 text-foreground-secondary" />
                            </div>
                            <div>
                                <p className="font-medium text-foreground">Clé publique (PK)</p>
                                <p className="text-sm text-foreground-secondary">
                                    {result.tests.publishableKey.message}
                                </p>
                            </div>
                        </div>
                        {getStatusIcon(result.tests.publishableKey.passed)}
                    </div>
                )}

                {/* Test webhook secret - Masqué si non fourni car généré automatiquement plus tard */}
                {result && result.tests.webhookSecret && !result.tests.webhookSecret.message.startsWith('Non fourni') && (
                    <div className="py-3 px-6 flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 rounded-lg bg-background-tertiary flex items-center justify-center">
                                <Webhook className="w-4 h-4 text-foreground-secondary" />
                            </div>
                            <div>
                                <p className="font-medium text-foreground">Webhook Secret</p>
                                <p className="text-sm text-foreground-secondary">
                                    {result.tests.webhookSecret.message}
                                </p>
                            </div>
                        </div>
                        {getStatusIcon(result.tests.webhookSecret.passed)}
                    </div>
                )}

                {/* Test permissions */}
                {result && (
                    <div className="py-3 px-6">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 rounded-lg bg-background-tertiary flex items-center justify-center">
                                    <Shield className="w-4 h-4 text-foreground-secondary" />
                                </div>
                                <div>
                                    <p className="font-medium text-foreground">Permissions API</p>
                                    <p className="text-sm text-foreground-secondary">
                                        {result.tests.permissions.message}
                                    </p>
                                </div>
                            </div>
                            {getStatusIcon(result.tests.permissions.passed)}
                        </div>

                        {/* Détails des permissions */}
                        {result.tests.permissions.details.length > 0 && (
                            <div className="mt-3 ml-11 space-y-1">
                                {result.tests.permissions.details.map((detail, index) => (
                                    <p key={index} className="text-xs text-foreground-tertiary">
                                        • {detail}
                                    </p>
                                ))}
                            </div>
                        )}

                        {/* Permissions dangereuses détectées */}
                        {result.permissions.dangerous.length > 0 && (
                            <div className="mt-3 ml-11 p-3 bg-error/10 border border-error/20 rounded-lg">
                                <div className="flex items-start space-x-2">
                                    <AlertTriangle className="w-4 h-4 text-error flex-shrink-0 mt-0.5" />
                                    <div>
                                        <p className="text-sm font-medium text-error">
                                            Permissions dangereuses détectées
                                        </p>
                                        <p className="text-xs text-foreground-secondary mt-1">
                                            {result.permissions.dangerous.join(', ')}
                                        </p>
                                        <p className="text-xs text-foreground-tertiary mt-2">
                                            Créez une clé API restreinte depuis votre dashboard Stripe avec uniquement
                                            la permission "PaymentIntents".
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Test PaymentIntent */}
                {result && (
                    <div className="py-3 px-6 flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 rounded-lg bg-background-tertiary flex items-center justify-center">
                                <CheckCircle className="w-4 h-4 text-foreground-secondary" />
                            </div>
                            <div>
                                <p className="font-medium text-foreground">Création de paiement</p>
                                <p className="text-sm text-foreground-secondary">
                                    {result.tests.paymentIntent.message}
                                </p>
                            </div>
                        </div>
                        {getStatusIcon(result.tests.paymentIntent.passed)}
                    </div>
                )}

                {/* Test Réception Webhook (si testé) */}
                {webhookTestResult?.tested && (
                    <div className="py-3 px-6 flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 rounded-lg bg-background-tertiary flex items-center justify-center">
                                <Webhook className="w-4 h-4 text-foreground-secondary" />
                            </div>
                            <div>
                                <p className="font-medium text-foreground">Réception Webhook</p>
                                <p className="text-sm text-foreground-secondary">
                                    {webhookTestResult.received
                                        ? 'CashLive a bien reçu et traité l\'\u00e9vénement de test'
                                        : 'L\'\u00e9vénement a \u00e9t\u00e9 envoy\u00e9 mais non confirm\u00e9'}
                                </p>
                            </div>
                        </div>
                        {webhookTestResult.received ? (
                            <CheckCircle className="w-5 h-5 text-success" />
                        ) : (
                            <AlertTriangle className="w-5 h-5 text-warning" />
                        )}
                    </div>
                )}
            </div>

            {/* Erreurs */}
            {result && result.errors.length > 0 && (
                <div className="bg-error/10 border border-error/20 rounded-xl p-4">
                    <div className="flex items-start space-x-3">
                        <AlertCircle className="w-5 h-5 text-error flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="font-medium text-error">Erreurs à corriger</p>
                            <ul className="mt-2 space-y-1">
                                {result.errors.map((error, index) => (
                                    <li key={index} className="text-sm text-foreground-secondary">
                                        • {error}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            )}

            {/* Avertissements (filtrer le mode test car affiché en haut) */}
            {(() => {
                if (!result) return null;
                const filteredWarnings = result.warnings.filter(w => !w.toLowerCase().includes('mode test'));
                return filteredWarnings.length > 0 && (
                    <div className="bg-warning/10 border border-warning/20 rounded-xl p-4">
                        <div className="flex items-start space-x-3">
                            <AlertTriangle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="font-medium text-warning">Recommandations</p>
                                <ul className="mt-2 space-y-1">
                                    {filteredWarnings.map((warning, index) => (
                                        <li key={index} className="text-sm text-foreground-secondary">
                                            • {warning}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Instructions pour créer une clé restreinte */}
            {result && !result.permissionsValid && (
                <div className="bg-info/10 border border-info/20 rounded-xl p-4">
                    <div className="flex items-start space-x-3">
                        <Info className="w-5 h-5 text-info flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="font-medium text-info">
                                Comment créer une clé API restreinte ?
                            </p>
                            <ol className="mt-2 space-y-2 text-sm text-foreground-secondary">
                                <li>1. Allez dans votre <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="underline font-medium text-info">Dashboard Stripe → Développeurs → Clés API</a></li>
                                <li>2. Cliquez sur "Créer une clé secrète restreinte"</li>
                                <li>3. Activez uniquement les permissions suivantes :
                                    <ul className="mt-1 ml-4 space-y-1">
                                        <li>• <strong>PaymentIntents</strong> : Écriture</li>
                                        <li>• <strong>PaymentMethods</strong> : Lecture</li>
                                        <li>• <strong>Customers</strong> : Écriture (optionnel)</li>
                                    </ul>
                                </li>
                                <li>4. Cliquez sur "Créer la clé"</li>
                            </ol>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StripeKeyValidationStatus;
